import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { ChangeEvent } from "react";
import { useAutoFocusOnEnter } from "@/shared/hooks/useAutoFocusOnEnter";
import { useTapInteraction } from "@/shared/ui/pressable";
import styles from "./VerificationCodeInput.module.scss";

type VerificationCodeInputProps = {
  label: string;
  value: string;
  placeholder?: string;
  timerText?: string;
  showTimer?: boolean;
  showResend?: boolean;
  resendDisabled?: boolean;
  disabled?: boolean;
  /** 진입 시 자동으로 row 를 펼치고 포커스한다(#210: row 노출 판정은 아래 isExpanded 참조). */
  autoActivate?: boolean;
  maxLength?: number;
  onChange: (value: string) => void;
  onResend: () => void;
};

const cx = (...classes: Array<string | false | undefined | null>) =>
  classes.filter(Boolean).join(" ");

const VerificationCodeInput = forwardRef<
  HTMLInputElement,
  VerificationCodeInputProps
>(
  (
    {
      label,
      value,
      placeholder,
      timerText,
      showTimer = false,
      showResend = false,
      resendDisabled = false,
      disabled = false,
      autoActivate = false,
      maxLength = 6,
      onChange,
      onResend,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

    const { isPressed: resendPressed, ref: resendRef, pressHandlers: resendHandlers } = useTapInteraction<HTMLButtonElement>({ disabled: resendDisabled || disabled });
    const [isFocused, setIsFocused] = useState(false);
    const [isActivated, setIsActivated] = useState(false);

    const hasValue = value.length > 0;
    // row(타이머·[재요청])는 포커스/값/활성화 중 하나면 펼친다.
    // - 활성화 전(OtpSentPopup 노출 중)에는 접는다(Figma 783-110473). autoActivate 이후엔 계속 펼침(783-110423).
    // - isActivated 는 sticky(아래 useEffect) → [재요청] 탭 시 input blur 로 접히지 않는다(#210·#265).
    //   showTimer/showResend 를 조건에 넣지 말 것 — 팝업 노출 중 필드가 강제로 펼쳐져 783-110473 과 어긋난다.
    // ⚠️ 불변식: showResend 소비처는 autoActivate 를 함께 넘긴다. 근본 차단은 [재요청] onMouseDown preventDefault(아래 button).
    const isExpanded = isFocused || hasValue || isActivated;

    // autoActivate 가 무장되면 즉시 row 를 펼친다(시각적 확장). 실제 focus() 호출은
    // useAutoFocusOnEnter 가 진입 전환 정착을 기다린 뒤 실행하므로 여기서는 확장만 담당한다.
    useEffect(() => {
      if (!autoActivate) return;
      setIsActivated(true);
    }, [autoActivate]);

    // hidden row/비활성 패널에서는 훅의 가시성 가드(aria-hidden 조상 검사)가 focus 를 막는다.
    useAutoFocusOnEnter(inputRef, { enabled: autoActivate });

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const filtered = e.target.value.replace(/[^0-9]/g, "");
      onChange(filtered);
    };

    const handleShellClick = () => {
      if (disabled) return;
      inputRef.current?.focus();
    };

    return (
      <div
        className={cx(
          styles.shell,
          isFocused && styles.shellFocused,
          disabled && styles.shellDisabled,
        )}
        data-expanded={isExpanded ? "true" : "false"}
        onClick={handleShellClick}
        role="presentation"
      >
        <span className={cx(styles.label, isExpanded && styles.labelCompact)}>
          {label}
        </span>
        <div
          className={cx(styles.row, !isExpanded && styles.rowHidden)}
          aria-hidden={!isExpanded}
        >
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={maxLength}
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label={`${label} ${maxLength}자리`}
          />
          {showTimer && timerText && (
            <span
              className={styles.timer}
              aria-live="polite"
              aria-label={`남은 시간 ${timerText}`}
            >
              {timerText}
            </span>
          )}
          {showResend && (
            <button
              ref={resendRef}
              type="button"
              className={`${styles.resendBtn} ${resendPressed ? styles.pressed : ""}`.trim()}
              // #210 근본 차단: mousedown 단계에서 focus 이동(=input blur)을 막아, 값이 비어도
              // row 가 접히지 않게 한다. touch 의 합성 click 은 취소되지 않으므로 아래 onClick 은 그대로 발화.
              // 액션/키보드(Enter·Space)는 onClick 유지, shell 이중발화 방지 stopPropagation 유지.
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                onResend();
              }}
              disabled={resendDisabled || disabled}
              aria-label="인증번호 재요청"
              tabIndex={isExpanded ? 0 : -1}
              {...resendHandlers}
            >
              재요청
            </button>
          )}
        </div>
      </div>
    );
  },
);

VerificationCodeInput.displayName = "VerificationCodeInput";

export default VerificationCodeInput;
export type { VerificationCodeInputProps };
